import { useState, useEffect, useCallback } from "react";
import { warroomApi, venturesApi } from "@/lib/api-client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Swords, Loader2, ChevronRight, ShieldAlert, Radar, Zap, ScanSearch, TrendingUp, TrendingDown } from "lucide-react";
import { fmtUSDFull, AGENT_META } from "@/lib/format";
import { toast } from "sonner";

const AGENT_ICONS = { scout: Radar, sentinel: ShieldAlert, executor: Zap, auditor: ScanSearch };

export default function WarRoom({ open, onOpenChange, onCreated }) {
  const [ventures, setVentures] = useState([]);
  const [ventureId, setVentureId] = useState("");
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  const resetForm = useCallback(() => {
    setPlan(null);
    setSituation("");
    setVentureId("");
  }, []);

  useEffect(() => {
    if (open) {
      venturesApi.list().then(setVentures).catch((e) => console.error("ventures load failed", e));
      resetForm();
    }
  }, [open, resetForm]);

  const generate = async () => {
    if (!ventureId) return toast.error("Select a venture");
    setBusy(true);
    try {
      const p = await warroomApi.createPlan(ventureId, situation);
      setPlan(p);
      toast.success("Battle Plan generated");
      onCreated?.();
    } catch (e) {
      console.error("war room create failed", e);
      toast.error("War Room failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border border-red-500/30 text-white max-w-3xl p-0 rounded-none max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="war-room"
      >
        <div className="p-6 border-b border-white/10 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.08), transparent)" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center border border-red-500/40 bg-black">
              <Swords size={16} className="text-red-400"/>
            </div>
            <div>
              <div className="uc-chip"><span className="uc-dot bg-red-500 uc-pulsing"></span> WAR ROOM · DEEP FOCUS</div>
              <div className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 mt-1">
                All four agents · Consolidated battle plan
              </div>
            </div>
          </div>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl text-white">Engage Deep Focus</DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 overflow-y-auto uc-scroll flex-1">
          {!plan ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Target Venture</label>
                <Select value={ventureId} onValueChange={setVentureId}>
                  <SelectTrigger data-testid="warroom-venture-select" className="h-11 rounded-none bg-[#121212] border-white/10">
                    <SelectValue placeholder="Choose venture for Deep Focus…"/>
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                    {ventures.map((v) => (
                      <SelectItem key={v.id} value={v.id} data-testid={`warroom-opt-${v.id}`}>
                        {v.name} · {fmtUSDFull(v.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Situation Briefing (optional)</label>
                <Textarea
                  data-testid="warroom-situation"
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  placeholder="Describe the situation: e.g. 'Revenue down 20% MoM, considering pivot or divestment.'"
                  className="min-h-24 rounded-none bg-[#121212] border-white/10 resize-none"
                />
              </div>
              <div className="p-4 border border-white/10 bg-black/40">
                <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-2">On Engagement</div>
                <ul className="space-y-1 text-xs text-white/60">
                  <li>· All 4 agents consolidate into a single Battle Plan</li>
                  <li>· Every step ships with Best-Case and Worst-Case projections</li>
                  <li>· Steps auto-populate as tasks on the Master Calendar</li>
                  <li>· A critical alert is dispatched (in-app + SMS if configured)</li>
                </ul>
              </div>
              <Button
                data-testid="warroom-engage-btn"
                onClick={generate}
                disabled={busy || !ventureId}
                className="w-full h-12 rounded-none uc-focus"
                style={{ background: "#EF4444", color: "#fff" }}
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={16}/> : <Swords size={16} className="mr-2"/>}
                Engage War Room
              </Button>
            </div>
          ) : (
            <div className="space-y-5" data-testid="warroom-plan">
              <div>
                <div className="uc-chip mb-2"><Swords size={11}/> BATTLE PLAN</div>
                <h3 className="font-serif-display text-2xl">{plan.title}</h3>
                <p className="text-white/60 text-sm mt-2">{plan.objective}</p>
              </div>
              <div className="space-y-3">
                {plan.steps.map((s, i) => {
                  const meta = AGENT_META[s.agent] || AGENT_META.scout;
                  const Icon = AGENT_ICONS[s.agent] || Radar;
                  return (
                    <div key={`${s.n}-${s.agent}`} className="border border-white/10 bg-black/40" data-testid={`warroom-step-${s.n}`}>
                      <div className="p-4 border-b border-white/5 flex items-center gap-3" style={{ borderLeft: `3px solid ${meta.color}` }}>
                        <div className="font-serif-display text-3xl text-white/30 w-10">{s.n}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon size={12} style={{ color: meta.color }}/>
                            <span className="font-mono-uc text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                            <span className="text-white/30 text-xs">· ETA {s.eta_days}d</span>
                            {s.cost_estimate > 0 && <span className="text-white/50 font-mono-uc text-[10px]">· {fmtUSDFull(s.cost_estimate)}</span>}
                          </div>
                          <div className="text-white/90 text-sm">{s.action}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/5">
                        <div className="p-3">
                          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1 mb-1">
                            <TrendingUp size={10}/> Best Case
                          </div>
                          <div className="text-white/70 text-xs">{s.best_case}</div>
                        </div>
                        <div className="p-3">
                          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-red-400 flex items-center gap-1 mb-1">
                            <TrendingDown size={10}/> Worst Case
                          </div>
                          <div className="text-white/70 text-xs">{s.worst_case}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="warroom-new-plan" onClick={() => setPlan(null)} variant="ghost" className="rounded-none h-11 border border-white/10 text-white/70 hover:bg-white/5">
                  New Plan
                </Button>
                <Button data-testid="warroom-close" onClick={() => onOpenChange(false)} className="uc-btn-amber rounded-none h-11">
                  Close War Room
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
